import { QuestionnaireItem } from '@medplum/fhirtypes'
import React from 'react'
import { BASE_API_URL } from 'utils/constants'
import { QUESTIONNAIRE_API_ENDPOINT_EXTENSION_URL } from 'fhir/Questionnaire/constants'
import { useDataFetch } from 'contexts/DataFetchContext'
import { HttpClient } from 'utils/httpClient'

interface FetcherParams {
  params?: Record<string, unknown>
  sectionData: QuestionnaireItem
}

interface FetcherResult {
  data: Record<string, unknown> | null
  loading: boolean
  error: string | null
}

const useDynamicDataFetcher = ({ sectionData }: FetcherParams): FetcherResult => {
  const [data, setData] = React.useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)

  const { searchQuery, offset } = useDataFetch()

  const apiEndpoints = React.useMemo(
    () =>
      sectionData?.extension
        ?.filter((ext) => ext.url === QUESTIONNAIRE_API_ENDPOINT_EXTENSION_URL)
        .map((ext) => ext.valueCodeableConcept?.coding?.[0]?.code),
    [sectionData]
  )

  const fetchApiData = React.useCallback(
    async (endpoint: string): Promise<unknown[] | null> => {
      try {
        const queryParams: string[] = []

        if (searchQuery.value && searchQuery.param) {
          queryParams.push(`${searchQuery.param}=${searchQuery.value}`)
        }

        if (offset) {
          queryParams.push(`offset=${offset}`)
        }

        const queryString = queryParams.length ? `?${queryParams.join('&')}` : ''
        const url = `${BASE_API_URL}/${endpoint}${queryString}`

        const response = await new HttpClient().fetch(url)
        if (!response.ok) {
          throw new Error(`Failed to fetch ${endpoint}: ${response.status}`)
        }
        return await response.json()
      } catch (err) {
        setError(`API Error: ${err}`)
        return null
      }
    },
    [searchQuery, offset]
  )

  React.useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const fetchedData: Record<string, unknown> = {}

      for (const endpoint of apiEndpoints || []) {
        if (endpoint) {
          const apiData = (await fetchApiData(endpoint)) as { data?: unknown[] } | null
          if (apiData && apiData.data) {
            if (offset > 0 && data && data[endpoint]) {
              fetchedData[endpoint] = [...(data[endpoint] as unknown[]), ...apiData.data]
            } else {
              fetchedData[endpoint] = [...apiData.data]
            }
          }
        }
      }
      setData(fetchedData)
      setLoading(false)
    }

    void fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, offset])

  return { data, loading, error }
}

export default useDynamicDataFetcher
